import { FC, useCallback, useState } from 'react';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Skeleton } from '@mui/material';
import RouteParam from '@/_constants/RouteParam.ts';
import useReservations from '@/api/useReservations.ts';
import ReservationsTable from '@/components/functional/ReservationsTable/ReservationsTable.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { passToRouteUrlParams } from '@/utils/passToRouteUrlParams.ts';
import Routes from '@/router/Routes.ts';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import CancelReservationModal from '@/components/modals/Reservation/CancelReservationModal.tsx';
import EditReservationModal from '@/components/modals/Reservation/EditReservationModal.tsx';

const ReservationsPage: FC = () => {
  const { t } = useTranslation();
  const [selectedElements, setSelectedElements] = useState<number[]>([]);
  const { isManager, user } = useAuthContext();
  const {
    data: reservations,
    isLoading: isLoadingReservations,
    isError: isErrorReservations,
    error: errorReservations,
  } = useReservations(!!user?.restaurant_id, {
    restaurant_id: user?.restaurant_id,
  });

  const handleSelectedElementChange = useCallback((selectedElements: number[]) => {
    setSelectedElements(selectedElements);
  }, []);

  const canEdit = selectedElements.length === 1;
  const canCancel = selectedElements.length === 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4">
        {isLoadingReservations ? (
          <Skeleton variant="text" width={200} />
        ) : reservations && reservations[0]?.restaurant_id?.name ? (
          <h1 className="text-3xl font-medium m-0 font-ubuntu">{reservations[0].restaurant_id.name}</h1>
        ) : (
          <h1 className="text-3xl font-medium m-0 font-ubuntu">{t('pages.restaurantReservations.title')}</h1>
        )}
      </div>
      {isErrorReservations && <Alert severity="error">{errorReservations?.message}</Alert>}
      <div className="flex justify-end flex-wrap gap-2">
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained" disabled={!canEdit}>
              {t('button.editReservation')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <EditReservationModal isOpen={isOpen} onClose={handleClose} reservationId={selectedElements[0]} />
          )}
        />
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="error" variant="contained" disabled={!canCancel}>
              {t('button.cancelReservation')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <CancelReservationModal isOpen={isOpen} onClose={handleClose} reservationId={selectedElements[0]} />
          )}
        />
      </div>
      <div>
        <LoaderWrapper isLoading={isLoadingReservations}>
          <ReservationsTable
            rows={reservations ?? []}
            selectable={isManager}
            onSelectedChange={handleSelectedElementChange}
            logsUrlGeneratorFn={(reservationId) =>
              passToRouteUrlParams(Routes.MANAGER__RESERVATIONS__LOGS, {
                [RouteParam.RESERVATION_ID]: reservationId,
              })
            }
          />
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default ReservationsPage;
